/**
 * Evaluates a parsed expression on `decimal.js` (plan §5.1, §5.8).
 *
 * Two behaviours are deliberate. `if(condition, yes, no)` evaluates **only the branch it takes**, so a
 * guard like `if(total > 0, x / total, 0)` is safe; and a division by zero or a non-finite result stops
 * the equation with a typed error instead of producing `Infinity` that would then be rounded and stored
 * (PP §7.5: invalid results block rather than being silently clipped).
 */
import { throwEngineError } from '../errors.js';
import { decimal, isWithinRange, Money } from '../money.js';
import { Decimal } from 'decimal.js';
const TRUE = new Money(1);
const FALSE = new Money(0);
const bool = (value) => (value ? TRUE : FALSE);
const guardFinite = (value, node) => {
    if (!isWithinRange(value)) {
        return throwEngineError('NON_FINITE', 'The result is not a finite number this platform can price', { position: node.start });
    }
    return value;
};
export const evaluateNode = (node, resolve) => {
    switch (node.type) {
        case 'number':
            return decimal(node.value);
        case 'variable':
            return resolve(node.name, node);
        case 'unary': {
            const operand = evaluateNode(node.operand, resolve);
            return node.op === '-' ? operand.negated() : operand;
        }
        case 'binary': {
            if (node.op === '/') {
                const left = evaluateNode(node.left, resolve);
                const right = evaluateNode(node.right, resolve);
                if (right.isZero()) {
                    return throwEngineError('DIVISION_BY_ZERO', 'Division by zero', { position: node.right.start });
                }
                return guardFinite(left.dividedBy(right), node);
            }
            const left = evaluateNode(node.left, resolve);
            const right = evaluateNode(node.right, resolve);
            switch (node.op) {
                case '+':
                    return guardFinite(left.plus(right), node);
                case '-':
                    return guardFinite(left.minus(right), node);
                case '*':
                    return guardFinite(left.times(right), node);
                case '^':
                    return guardFinite(left.pow(right), node);
                case '<':
                    return bool(left.lessThan(right));
                case '>':
                    return bool(left.greaterThan(right));
                case '<=':
                    return bool(left.lessThanOrEqualTo(right));
                case '>=':
                    return bool(left.greaterThanOrEqualTo(right));
                case '==':
                    return bool(left.equals(right));
                case '!=':
                    return bool(!left.equals(right));
                default:
                    return throwEngineError('SYNTAX', `Unsupported operator "${node.op}"`, { position: node.start });
            }
        }
        case 'call': {
            switch (node.name) {
                case 'if': {
                    const condition = evaluateNode(node.args[0], resolve);
                    return evaluateNode(condition.isZero() ? node.args[2] : node.args[1], resolve);
                }
                case 'min': {
                    const values = node.args.map((arg) => evaluateNode(arg, resolve));
                    return values.reduce((smallest, value) => (value.lessThan(smallest) ? value : smallest));
                }
                case 'max': {
                    const values = node.args.map((arg) => evaluateNode(arg, resolve));
                    return values.reduce((largest, value) => (value.greaterThan(largest) ? value : largest));
                }
                case 'round':
                    return guardFinite(evaluateNode(node.args[0], resolve).toDecimalPlaces(0, Decimal.ROUND_HALF_UP), node);
                case 'ceil':
                    return guardFinite(evaluateNode(node.args[0], resolve).ceil(), node);
                case 'floor':
                    return guardFinite(evaluateNode(node.args[0], resolve).floor(), node);
                case 'abs':
                    return guardFinite(evaluateNode(node.args[0], resolve).abs(), node);
                default:
                    // Unreachable: the parser only ever builds calls to the functions above.
                    return throwEngineError('UNKNOWN_FUNCTION', 'Unsupported function', {});
            }
        }
        default:
            return throwEngineError('SYNTAX', 'Unsupported expression', {});
    }
};
/**
 * The substituted form the valuation breakdown and the equation editor print: the same text the admin
 * wrote, with every *variable* replaced by the number it resolved to. Function names, operators,
 * brackets and spacing survive untouched, so `max(1, valuationYear - modelYear + 1)` reads back as
 * `max(1, 2026 - 2025 + 1)`.
 */
export const substituteExpression = (source, tokens, values) => {
    let result = '';
    let cursor = 0;
    for (const token of tokens) {
        if (token.type !== 'identifier') {
            continue;
        }
        const replacement = values.get(token.value);
        if (replacement === undefined) {
            continue;
        }
        result += source.slice(cursor, token.start) + replacement;
        cursor = token.end;
    }
    return result + source.slice(cursor);
};
//# sourceMappingURL=evaluator.js.map